import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { getCookie } from "../../tools/tools";

const FALLBACK_CLASSES = [
  { id: 0, name: "Actinomorphic" },
  { id: 1, name: "Asymmetrical" },
  { id: 2, name: "Elliptica" },
  { id: 3, name: "Fragmentata" },
  { id: 4, name: "Fusiform" },
  { id: 5, name: "Heteropolar bilateral" },
  { id: 6, name: "Isopolar dorsiventral" },
  { id: 7, name: "Lanceolata" },
  { id: 8, name: "Linearis" },
  { id: 9, name: "Pandurata" },
  { id: 10, name: "Rhombica" },
  { id: 11, name: "Иные изобилатеральные" },
];

const COLOR_PALETTE = [
  "#e63946",
  "#ff7f11",
  "#ffd166",
  "#80ed99",
  "#06d6a0",
  "#118ab2",
  "#073b4c",
  "#7b2cbf",
  "#f72585",
  "#8ecae6",
  "#ff006e",
  "#3a86ff",
];

const getColorByClassId = (classId) => COLOR_PALETTE[classId % COLOR_PALETTE.length];

const toPointString = (points) => points.map((p) => `${p[0]},${p[1]}`).join(" ");
const getPolygonBounds = (points) => {
  if (!Array.isArray(points) || points.length === 0) return null;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX,
    maxX,
    minY,
    maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

const metricToPercent = (value) => `${(100 * (value || 0)).toFixed(2)}%`;

const MIN_ZOOM = 50;
const MAX_ZOOM = 300;
const MIN_POINT_DISTANCE = 0.003;

const buildCursor = (color) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path d='M3 19.8 4.3 23l3.1-1.3L19.6 9.4 14.6 4.4z' fill='${color}' stroke='#111' stroke-width='1.2' stroke-linejoin='round'/><path d='M14.6 4.4 16.8 2.2a1.6 1.6 0 0 1 2.2 0l2.8 2.8a1.6 1.6 0 0 1 0 2.2l-2.2 2.2z' fill='#f4d35e' stroke='#111' stroke-width='1.2' stroke-linejoin='round'/><path d='m3 19.8 2.7 1.2 1.7-1.7-2.2-2.2z' fill='#f8f9fa' stroke='#111' stroke-width='1.1' stroke-linejoin='round'/><path d='M2.9 22.9 4 20.3l1.7 1.7z' fill='#111' stroke='#111' stroke-width='0.8' stroke-linejoin='round'/></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") 3 21, crosshair`;
};

const PlanktonSegmentationExamV1 = () => {
  const [classes, setClasses] = useState(FALLBACK_CLASSES);
  const [activeClassId, setActiveClassId] = useState(0);
  const [sample, setSample] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [draftPoints, setDraftPoints] = useState([]);
  const [gtAnnotations, setGtAnnotations] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [perClass, setPerClass] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(10);
  const [attemptsUnlimited, setAttemptsUnlimited] = useState(false);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [draggingVertex, setDraggingVertex] = useState(null);
  const [isFreeDrawing, setIsFreeDrawing] = useState(false);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [activeGtClassId, setActiveGtClassId] = useState(null);
  const [activeGtIndex, setActiveGtIndex] = useState(0);
  const svgRef = useRef(null);
  const canvasScrollRef = useRef(null);
  const stageRef = useRef(null);

  const classById = useMemo(() => {
    const m = new Map();
    classes.forEach((c) => m.set(c.id, c));
    return m;
  }, [classes]);

  const groupedStudentAnnotations = useMemo(() => {
    const grouped = new Map();
    annotations.forEach((ann) => {
      if (!grouped.has(ann.class_id)) grouped.set(ann.class_id, []);
      grouped.get(ann.class_id).push(ann);
    });
    return grouped;
  }, [annotations]);

  const groupedGtAnnotations = useMemo(() => {
    const grouped = new Map();
    gtAnnotations.forEach((ann, globalIndex) => {
      if (!grouped.has(ann.class_id)) grouped.set(ann.class_id, []);
      grouped.get(ann.class_id).push({ ann, globalIndex });
    });
    return grouped;
  }, [gtAnnotations]);

  const gtClassIds = useMemo(() => {
    return classes
      .map((c) => c.id)
      .filter((classId) => (groupedGtAnnotations.get(classId) || []).length > 0);
  }, [classes, groupedGtAnnotations]);

  const activeGtEntries = useMemo(
    () => (activeGtClassId === null ? [] : groupedGtAnnotations.get(activeGtClassId) || []),
    [groupedGtAnnotations, activeGtClassId]
  );

  const activeGtEntry = useMemo(
    () => (activeGtEntries.length > 0 ? activeGtEntries[Math.min(activeGtIndex, activeGtEntries.length - 1)] : null),
    [activeGtEntries, activeGtIndex]
  );

  const activeGtGlobalIndex = activeGtEntry?.globalIndex ?? null;

  const loadSample = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await axios.get("/api/plankton-seg-exam/v1/sample", {
        withCredentials: true,
      });
      const cls = Array.isArray(response.data.class_names) && response.data.class_names.length > 0
        ? response.data.class_names
        : FALLBACK_CLASSES;
      setClasses(cls);
      setActiveClassId(cls[0].id);
      setSample({
        sample_id: response.data.sample_id,
        image_url: response.data.image_url,
      });
      if (typeof response.data.attempts_unlimited === "boolean") {
        setAttemptsUnlimited(response.data.attempts_unlimited);
      } else {
        setAttemptsUnlimited(false);
      }
      if (typeof response.data.remaining_attempts === "number") {
        setRemainingAttempts(response.data.remaining_attempts);
      } else {
        setRemainingAttempts(null);
      }
      if (typeof response.data.max_attempts === "number") {
        setMaxAttempts(response.data.max_attempts);
      } else {
        setMaxAttempts(null);
      }
      setAnnotations([]);
      setSelectedAnnotationId(null);
      setDraftPoints([]);
      setGtAnnotations([]);
      setActiveGtClassId(null);
      setActiveGtIndex(0);
      setMetrics(null);
      setPerClass([]);
      setZoomPercent(100);
    } catch (e) {
      const data = e?.response?.data || {};
      if (typeof data.attempts_unlimited === "boolean") {
        setAttemptsUnlimited(data.attempts_unlimited);
      }
      if (typeof data.remaining_attempts === "number") {
        setRemainingAttempts(data.remaining_attempts);
      } else if (data.attempts_unlimited === true) {
        setRemainingAttempts(null);
      }
      if (typeof data.max_attempts === "number") {
        setMaxAttempts(data.max_attempts);
      } else if (data.attempts_unlimited === true) {
        setMaxAttempts(null);
      }
      setError(data.error || e.message || "Failed to load sample");
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    loadSample();
  }, []);

  const addPoint = (evt, force = false) => {
    if (!sample) return;
    const nextPoint = getNormalizedPointFromEvent(evt);
    if (!nextPoint) return;

    setDraftPoints((prev) => {
      if (!force && prev.length > 0) {
        const [lastX, lastY] = prev[prev.length - 1];
        const dx = nextPoint[0] - lastX;
        const dy = nextPoint[1] - lastY;
        if (Math.hypot(dx, dy) < MIN_POINT_DISTANCE) {
          return prev;
        }
      }
      return [...prev, nextPoint];
    });
  };

  const getNormalizedPointFromEvent = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const x = (evt.clientX - rect.left) / rect.width;
    const y = (evt.clientY - rect.top) / rect.height;

    return [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))];
  };

  const startVertexDrag = (evt, target, vertexIndex) => {
    evt.preventDefault();
    evt.stopPropagation();
    setDraggingVertex({ target, vertexIndex });
  };

  const handleSvgMouseDown = (evt) => {
    if (!sample || draggingVertex) return;
    setIsFreeDrawing(true);
    addPoint(evt, true);
  };

  const stopDrawingInteractions = () => {
    if (draggingVertex) {
      setDraggingVertex(null);
    }
    if (isFreeDrawing) {
      setIsFreeDrawing(false);
    }
  };

  const handleSvgMouseMove = (evt) => {
    if (draggingVertex) {
      const nextPoint = getNormalizedPointFromEvent(evt);
      if (!nextPoint) return;

      if (draggingVertex.target.type === "annotation") {
        setAnnotations((prev) =>
          prev.map((ann) => {
            if (ann.id !== draggingVertex.target.annotationId) return ann;
            const nextPoints = ann.points.map((point, idx) =>
              idx === draggingVertex.vertexIndex ? nextPoint : point
            );
            return { ...ann, points: nextPoints };
          })
        );
        return;
      }

      if (draggingVertex.target.type === "draft") {
        setDraftPoints((prev) =>
          prev.map((point, idx) => (idx === draggingVertex.vertexIndex ? nextPoint : point))
        );
      }
      return;
    }

    if (isFreeDrawing) {
      addPoint(evt);
    }
  };

  const finishPolygon = () => {
    if (draftPoints.length < 3) {
      setDraftPoints([]);
      return;
    }
    const newAnn = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      class_id: activeClassId,
      points: draftPoints,
    };
    setAnnotations((prev) => [...prev, newAnn]);
    setSelectedAnnotationId(newAnn.id);
    setDraftPoints([]);
  };

  const cancelDraft = () => setDraftPoints([]);

  const removeAnnotation = (id) => {
    setAnnotations((prev) => prev.filter((ann) => ann.id !== id));
    setSelectedAnnotationId((prev) => (prev === id ? null : prev));
  };

  const focusPointsInView = (points) => {
    const bounds = getPolygonBounds(points);
    if (!bounds) return;

    const scrollEl = canvasScrollRef.current;
    const stageEl = stageRef.current;
    if (!scrollEl || !stageEl) return;

    const targetX = bounds.centerX * stageEl.clientWidth;
    const targetY = bounds.centerY * stageEl.clientHeight;
    const nextLeft = Math.max(0, targetX - scrollEl.clientWidth / 2);
    const nextTop = Math.max(0, targetY - scrollEl.clientHeight / 2);

    scrollEl.scrollTo({ left: nextLeft, top: nextTop, behavior: "smooth" });
  };

  const focusAnnotationInView = (annotationId) => {
    const ann = annotations.find((item) => item.id === annotationId);
    if (!ann) return;
    focusPointsInView(ann.points);
  };

  const selectAnnotation = (annotationId) => {
    setSelectedAnnotationId(annotationId);
    focusAnnotationInView(annotationId);
  };

  const selectGtClass = (classId) => {
    setActiveGtClassId(classId);
    setActiveGtIndex(0);
  };

  const showPrevGt = () => {
    if (activeGtEntries.length === 0) return;
    setActiveGtIndex((prev) => (prev - 1 + activeGtEntries.length) % activeGtEntries.length);
  };

  const showNextGt = () => {
    if (activeGtEntries.length === 0) return;
    setActiveGtIndex((prev) => (prev + 1) % activeGtEntries.length);
  };

  const submitForEvaluation = async () => {
    if (!sample) return;
    setBusy(true);
    setError("");
    try {
      const payload = {
        sample_id: sample.sample_id,
        annotations: annotations.map((ann) => ({
          class_id: ann.class_id,
          points: ann.points,
        })),
      };
      const response = await axios.post("/api/plankton-seg-exam/v1/evaluate", payload, {
        headers: {
          "X-CSRFToken": getCookie("csrftoken"),
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      setMetrics(response.data.metrics || null);
      setPerClass(response.data.per_class || []);
      setGtAnnotations(response.data.gt_annotations || []);
      if (typeof response.data.attempts_unlimited === "boolean") {
        setAttemptsUnlimited(response.data.attempts_unlimited);
      }
      if (typeof response.data.remaining_attempts === "number") {
        setRemainingAttempts(response.data.remaining_attempts);
      } else if (response.data.attempts_unlimited === true) {
        setRemainingAttempts(null);
      }
      if (typeof response.data.max_attempts === "number") {
        setMaxAttempts(response.data.max_attempts);
      } else if (response.data.attempts_unlimited === true) {
        setMaxAttempts(null);
      }
    } catch (e) {
      const errs = e?.response?.data?.errors;
      const data = e?.response?.data || {};
      if (typeof data.attempts_unlimited === "boolean") {
        setAttemptsUnlimited(data.attempts_unlimited);
      }
      if (typeof data.remaining_attempts === "number") {
        setRemainingAttempts(data.remaining_attempts);
      } else if (data.attempts_unlimited === true) {
        setRemainingAttempts(null);
      }
      if (typeof data.max_attempts === "number") {
        setMaxAttempts(data.max_attempts);
      } else if (data.attempts_unlimited === true) {
        setMaxAttempts(null);
      }
      if (errs) {
        setError(typeof errs === "string" ? errs : JSON.stringify(errs));
      } else {
        setError(e.message || "Evaluation failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const activeClassName = classById.get(activeClassId)?.name || `Class ${activeClassId}`;
  const activeCursor = buildCursor(getColorByClassId(activeClassId));
  const hasRevealedAnswers = gtAnnotations.length > 0;

  const zoomIn = () => setZoomPercent((z) => Math.min(MAX_ZOOM, z + 10));
  const zoomOut = () => setZoomPercent((z) => Math.max(MIN_ZOOM, z - 10));
  const resetZoom = () => setZoomPercent(100);

  React.useEffect(() => {
    if (gtClassIds.length === 0) {
      setActiveGtClassId(null);
      setActiveGtIndex(0);
      return;
    }

    if (activeGtClassId === null || !gtClassIds.includes(activeGtClassId)) {
      setActiveGtClassId(gtClassIds[0]);
      setActiveGtIndex(0);
      return;
    }

    const entries = groupedGtAnnotations.get(activeGtClassId) || [];
    if (entries.length === 0) {
      setActiveGtIndex(0);
      return;
    }

    if (activeGtIndex >= entries.length) {
      setActiveGtIndex(entries.length - 1);
    }
  }, [gtClassIds, activeGtClassId, activeGtIndex, groupedGtAnnotations]);

  React.useEffect(() => {
    if (!activeGtEntry) return;
    focusPointsInView(activeGtEntry.ann.points);
  }, [activeGtEntry]);

  return (
    <div className="container-fluid pt-4 pb-4">
      <div className="row">
        <div className="col-12 mb-3">
          <h2>Plankton Segmentation Exam</h2>
          <p className="text-muted mb-0">
            Select a class, click vertices or hold and drag to trace continuously, then submit for server-side evaluation.
          </p>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 col-md-4 mb-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <button className="btn btn-primary btn-sm mb-3" onClick={loadSample} disabled={busy}>
                New random image
              </button>
              <button
                className="btn btn-success btn-sm mb-3 ml-2"
                onClick={submitForEvaluation}
                disabled={busy || !sample || (!attemptsUnlimited && remainingAttempts === 0) || hasRevealedAnswers}
              >
                Submit and score
              </button>

              <div className="small text-muted mb-3">
                Attempts: {attemptsUnlimited ? "unlimited" : (remainingAttempts === null ? "..." : `${remainingAttempts}/${maxAttempts} left`)}
              </div>

              <h5>Zoom</h5>
              <div className="d-flex align-items-center mb-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={zoomOut} disabled={zoomPercent <= MIN_ZOOM}>
                  -
                </button>
                <button className="btn btn-outline-secondary btn-sm ml-2" onClick={zoomIn} disabled={zoomPercent >= MAX_ZOOM}>
                  +
                </button>
                <button className="btn btn-outline-secondary btn-sm ml-2" onClick={resetZoom} disabled={zoomPercent === 100}>
                  100%
                </button>
                <span className="small ml-2">{zoomPercent}%</span>
              </div>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={10}
                value={zoomPercent}
                onChange={(e) => setZoomPercent(parseInt(e.target.value, 10))}
                style={{ width: "100%" }}
              />

              <h5>Classes</h5>
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                {classes.map((cls) => {
                  const color = getColorByClassId(cls.id);
                  const active = activeClassId === cls.id;
                  return (
                    <button
                      key={cls.id}
                      type="button"
                      className={`btn btn-sm mb-2 mr-2 ${active ? "btn-dark" : "btn-outline-secondary"}`}
                      onClick={() => setActiveClassId(cls.id)}
                      title={cls.name}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: color,
                          marginRight: 8,
                          border: "1px solid #111",
                          verticalAlign: "middle",
                        }}
                      />
                      {cls.id}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 p-2" style={{ border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="small text-muted">Active class</div>
                <div style={{ fontWeight: 600 }}>{activeClassName}</div>
                <div className="small text-muted mt-2">Pencil color</div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      marginRight: 8,
                      backgroundColor: getColorByClassId(activeClassId),
                      border: "1px solid #111",
                    }}
                  />
                  <span className="small">Used for next polygon</span>
                </div>
              </div>

              <h5 className="mt-4">Objects by class</h5>
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                {classes.map((cls) => {
                  const list = groupedStudentAnnotations.get(cls.id) || [];
                  if (list.length === 0) return null;
                  return (
                    <div key={`group-${cls.id}`} className="mb-2">
                      <div className="small text-muted">
                        {cls.name} ({list.length})
                      </div>
                      {list.map((ann, idx) => (
                        <div
                          key={ann.id}
                          className="d-flex justify-content-between align-items-center mb-1"
                          onClick={() => selectAnnotation(ann.id)}
                          style={{
                            cursor: "pointer",
                            border: selectedAnnotationId === ann.id ? "1px solid #1f6feb" : "1px solid transparent",
                            background: selectedAnnotationId === ann.id ? "#eaf2ff" : "transparent",
                            borderRadius: 6,
                            padding: "4px 6px",
                          }}
                        >
                          <span className="small" style={{ fontWeight: selectedAnnotationId === ann.id ? 700 : 400 }}>
                            #{idx + 1}, {ann.points.length} pts
                          </span>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={(evt) => {
                              evt.stopPropagation();
                              removeAnnotation(ann.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              <div className="mt-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={finishPolygon} disabled={draftPoints.length < 3}>
                  Close polygon
                </button>
                <button className="btn btn-outline-secondary btn-sm ml-2" onClick={cancelDraft} disabled={draftPoints.length === 0}>
                  Cancel draft
                </button>
              </div>
            </div>
          </div>

          {metrics && (
            <div className="card shadow-sm mt-3">
              <div className="card-body">
                <h5>Metrics</h5>
                <div className="small text-muted mb-1">
                  Evaluated classes: {metrics.evaluated_classes}/{metrics.total_classes}
                </div>
                <div className="small">mIoU: <b>{metricToPercent(metrics.miou)}</b></div>
                <div className="small">Instance F1@50 (macro): <b>{metricToPercent(metrics.instance_f1_50_macro)}</b></div>
                <div className="small">mAP50 proxy (macro): <b>{metricToPercent(metrics.map50_proxy_macro)}</b></div>
                <div className="small">PQ50 (macro): <b>{metricToPercent(metrics.pq50_macro)}</b></div>

                {metrics.overall && (
                  <>
                    <hr />
                    <div className="small text-muted mb-1">Overall plankton (all classes merged)</div>
                    <div className="small">GT instances: <b>{metrics.overall.gt_instances}</b></div>
                    <div className="small">Pred instances: <b>{metrics.overall.pred_instances}</b></div>
                    <div className="small">IoU: <b>{metricToPercent(metrics.overall.iou)}</b></div>
                    <div className="small">Instance F1@50: <b>{metricToPercent(metrics.overall.instance_f1_50)}</b></div>
                    <div className="small">AP50 proxy: <b>{metricToPercent(metrics.overall.ap50_proxy)}</b></div>
                    <div className="small">PQ50: <b>{metricToPercent(metrics.overall.pq50)}</b></div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-9 col-md-8">
          <div className="card shadow-sm">
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}
              {!sample && (
                <div className="d-flex align-items-center text-muted" style={{ minHeight: 48 }}>
                  <div className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                  <span>Loading sample...</span>
                </div>
              )}

              {sample && (
                <div ref={canvasScrollRef} style={{ overflowX: "auto", overflowY: "auto", maxHeight: "75vh" }}>
                  <div
                    ref={stageRef}
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: `${zoomPercent}%`,
                      minWidth: "100%",
                    }}
                  >
                    <img
                      src={sample.image_url}
                      alt="exam sample"
                      style={{ width: "100%", borderRadius: 12, border: "1px solid #ddd" }}
                    />

                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 5,
                        background: "rgba(255,255,255,0.95)",
                        border: "1px solid #d6d6d6",
                        borderRadius: 10,
                        padding: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={zoomOut}
                        disabled={zoomPercent <= MIN_ZOOM}
                        title="Zoom out"
                      >
                        -
                      </button>
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={zoomIn}
                        disabled={zoomPercent >= MAX_ZOOM}
                        title="Zoom in"
                      >
                        +
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={resetZoom}
                        disabled={zoomPercent === 100}
                        title="Reset zoom"
                      >
                        100%
                      </button>
                      <span className="small font-weight-bold">{zoomPercent}%</span>
                    </div>

                    <svg
                      ref={svgRef}
                      viewBox="0 0 1 1"
                      preserveAspectRatio="none"
                      onMouseDown={handleSvgMouseDown}
                      onDoubleClick={finishPolygon}
                      onMouseMove={handleSvgMouseMove}
                      onMouseUp={stopDrawingInteractions}
                      onMouseLeave={stopDrawingInteractions}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        finishPolygon();
                      }}
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        cursor: activeCursor,
                      }}
                    >
                      {annotations.map((ann) => {
                        const color = getColorByClassId(ann.class_id);
                        const isSelected = ann.id === selectedAnnotationId;
                        return (
                          <g key={`student-${ann.id}`}>
                            <polygon
                              points={toPointString(ann.points)}
                              fill={color}
                              fillOpacity={isSelected ? 0.4 : 0.28}
                              stroke={color}
                              strokeWidth={isSelected ? 0.004 : 0.002}
                            />
                            {isSelected && (
                              <polygon
                                points={toPointString(ann.points)}
                                fill="none"
                                stroke="#ffffff"
                                strokeWidth={0.0017}
                              />
                            )}
                            {ann.points.map((p, idx) => (
                              <circle
                                key={`student-${ann.id}-vertex-${idx}`}
                                cx={p[0]}
                                cy={p[1]}
                                r={isSelected ? 0.0028 : 0.002}
                                fill="#ffffff"
                                stroke={color}
                                strokeWidth={0.002}
                                style={{ cursor: "grab" }}
                                onMouseDown={(evt) => startVertexDrag(evt, { type: "annotation", annotationId: ann.id }, idx)}
                              />
                            ))}
                          </g>
                        );
                      })}

                      {draftPoints.length > 0 && (
                        <>
                          <polyline
                            points={toPointString(draftPoints)}
                            fill="none"
                            stroke={getColorByClassId(activeClassId)}
                            strokeWidth={0.0027}
                          />
                          {draftPoints.map((p, i) => (
                            <circle
                              key={`draft-point-${i}`}
                              cx={p[0]}
                              cy={p[1]}
                              r={0.002}
                              fill="#ffffff"
                              stroke={getColorByClassId(activeClassId)}
                              strokeWidth={0.002}
                              style={{ cursor: "grab" }}
                              onMouseDown={(evt) => startVertexDrag(evt, { type: "draft" }, i)}
                            />
                          ))}
                        </>
                      )}

                      {gtAnnotations.map((ann, idx) => {
                        const color = getColorByClassId(ann.class_id);
                        const isActiveGt = idx === activeGtGlobalIndex;
                        return (
                          <g key={`gt-${idx}`}>
                            {isActiveGt && (
                              <polygon
                                points={toPointString(ann.points)}
                                fill={color}
                                fillOpacity={0.12}
                                stroke="none"
                              />
                            )}
                            <polygon
                              points={toPointString(ann.points)}
                              fill="none"
                              stroke={isActiveGt ? "#ffffff" : color}
                              strokeDasharray="0.01 0.01"
                              strokeWidth={isActiveGt ? 0.004 : 0.002}
                            />
                            {isActiveGt && (
                              <polygon
                                points={toPointString(ann.points)}
                                fill="none"
                                stroke={color}
                                strokeWidth={0.0027}
                              />
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                </div>
              )}

              {gtAnnotations.length > 0 && (
                <div className="mt-3 alert alert-info mb-0">
                  <div className="small">Ground-truth polygons are displayed as dashed outlines.</div>
                  {activeGtClassId !== null && (
                    <div className="d-flex align-items-center mt-2" style={{ gap: 8, flexWrap: "wrap" }}>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={showPrevGt}
                        disabled={activeGtEntries.length <= 1}
                        title="Previous GT object"
                      >
                        ←
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={showNextGt}
                        disabled={activeGtEntries.length <= 1}
                        title="Next GT object"
                      >
                        →
                      </button>
                      <span className="small" style={{ fontWeight: 600 }}>
                        {classById.get(activeGtClassId)?.name || `Class ${activeGtClassId}`} {activeGtEntries.length > 0 ? `${Math.min(activeGtIndex + 1, activeGtEntries.length)}/${activeGtEntries.length}` : "0/0"}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {perClass.some((row) => row.gt_instances > 0) && (
            <div className="card shadow-sm mt-3">
              <div className="card-body">
                <h5>Per-class summary</h5>
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Class</th>
                        <th>GT</th>
                        <th>Pred</th>
                        <th>IoU</th>
                        <th>F1@50</th>
                        <th>AP50 proxy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perClass.filter((row) => row.gt_instances > 0).map((row) => {
                        const isGtClassActive = row.class_id === activeGtClassId;
                        return (
                        <tr
                          key={`row-${row.class_id}`}
                          onClick={() => selectGtClass(row.class_id)}
                          style={{ cursor: "pointer", backgroundColor: isGtClassActive ? "#eef4ff" : undefined }}
                        >
                          <td>{row.class_name}</td>
                          <td>{row.gt_instances}</td>
                          <td>{row.pred_instances}</td>
                          <td>{metricToPercent(row.iou)}</td>
                          <td>{metricToPercent(row.instance_f1_50)}</td>
                          <td>{metricToPercent(row.ap50_proxy)}</td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanktonSegmentationExamV1;
